import React, { useEffect, useMemo, useState } from 'react';

// INTERNAL Growth Dashboard — Audee's command center. Rendered as a tab inside
// Rate Admin (AdminDashboard). Not customer-facing. All numbers are derived live
// by the backend on each load (/api/admin/growth/summary).
const API = 'https://tm-audit-platform-production.up.railway.app';

const fmt$ = (n) => '$' + Math.round(n || 0).toLocaleString();
const fmtNum = (n) => (n || 0).toLocaleString();
const fmt1 = (n) => (n == null ? '—' : (Math.round((n) * 10) / 10).toLocaleString());
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtDuration = (ms) => {
    if (ms == null) return '—';
    const days = ms / 86400000;
    if (days >= 1) return `${Math.round(days * 10) / 10}d`;
    const hrs = ms / 3600000;
    if (hrs >= 1) return `${Math.round(hrs * 10) / 10}h`;
    return `${Math.round(ms / 60000)}m`;
};
// Runtime (audit compute time) — sub-second to seconds scale.
const fmtMs = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)}s`;
};
const fmtMonth = (k) => {
    if (!k) return '—';
    const [y, m] = k.split('-');
    return new Date(Date.UTC(+y, +m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

// ── lightweight inline SVG charts (no external dependency) ───────────────────
function LineChart({ data, valueKey, color = 'var(--amber)', height = 120, money }) {
    const w = 640, h = height, pad = 6;
    if (!data || !data.length) return <div className="gd-chart-empty">No data</div>;
    const vals = data.map(d => d[valueKey] || 0);
    const max = Math.max(1, ...vals);
    const step = (w - pad * 2) / Math.max(1, data.length - 1);
    const pts = data.map((d, i) => {
        const x = pad + i * step;
        const y = h - pad - ((d[valueKey] || 0) / max) * (h - pad * 2);
        return [x, y];
    });
    const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
    const area = `${line} L ${pts[pts.length - 1][0].toFixed(1)} ${h - pad} L ${pad} ${h - pad} Z`;
    return (
        <svg className="gd-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
            <path d={area} fill={color} opacity="0.10" />
            <path d={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
            {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="1.6" fill={color} />)}
            <text x={pad} y={12} className="gd-svg-label">{money ? fmt$(max) : max}</text>
        </svg>
    );
}

// Vertical bars for monthly series (Part 8). Lightweight inline SVG, no dep.
function MonthBars({ data, valueKey, color = 'var(--amber)', money }) {
    if (!data || !data.length) return <div className="gd-chart-empty">No data</div>;
    const vals = data.map(d => d[valueKey] || 0);
    const max = Math.max(1, ...vals);
    return (
        <div className="gd-monthbars">
            {data.map((d, i) => (
                <div className="gd-mb-col" key={d.month || i} title={`${fmtMonth(d.month)}: ${money ? fmt$(d[valueKey]) : fmtNum(d[valueKey])}`}>
                    <div className="gd-mb-bar-wrap">
                        <div className="gd-mb-bar" style={{ height: `${((d[valueKey] || 0) / max) * 100}%`, background: color }} />
                    </div>
                    <div className="gd-mb-label">{fmtMonth(d.month).split(' ')[0]}</div>
                </div>
            ))}
        </div>
    );
}

function BarList({ rows, valueKey, labelKey = 'label', money, color = 'var(--amber)', max: maxRows = 8 }) {
    const items = (rows || []).slice(0, maxRows);
    if (!items.length) return <div className="gd-chart-empty">No data yet</div>;
    const max = Math.max(1, ...items.map(r => r[valueKey] || 0));
    return (
        <div className="gd-barlist">
            {items.map((r, i) => (
                <div className="gd-bar-row" key={r.id || i}>
                    <div className="gd-bar-label" title={r[labelKey]}>{r[labelKey]}</div>
                    <div className="gd-bar-track">
                        <div className="gd-bar-fill" style={{ width: `${((r[valueKey] || 0) / max) * 100}%`, background: color }} />
                    </div>
                    <div className="gd-bar-val">{money ? fmt$(r[valueKey]) : fmtNum(r[valueKey])}</div>
                </div>
            ))}
        </div>
    );
}

function Donut({ a, b, labelA, labelB }) {
    const total = (a || 0) + (b || 0);
    const pct = total ? (a / total) : 0;
    const C = 2 * Math.PI * 42;
    return (
        <div className="gd-donut-wrap">
            <svg viewBox="0 0 100 100" className="gd-donut">
                <circle cx="50" cy="50" r="42" fill="none" stroke="var(--surface3)" strokeWidth="12" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="var(--green)" strokeWidth="12"
                    strokeDasharray={`${(C * pct).toFixed(1)} ${C.toFixed(1)}`} transform="rotate(-90 50 50)" strokeLinecap="round" />
                <text x="50" y="54" textAnchor="middle" className="gd-donut-center">{Math.round(pct * 100)}%</text>
            </svg>
            <div className="gd-donut-legend">
                <div><span className="gd-dot" style={{ background: 'var(--green)' }} /> {labelA}: <b>{fmtNum(a)}</b></div>
                <div><span className="gd-dot" style={{ background: 'var(--surface3)' }} /> {labelB}: <b>{fmtNum(b)}</b></div>
            </div>
        </div>
    );
}

function KpiCard({ label, value, accent, sub }) {
    return (
        <div className="gd-kpi">
            <div className="gd-kpi-label">{label}</div>
            <div className="gd-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
            {sub && <div className="gd-kpi-sub">{sub}</div>}
        </div>
    );
}

export default function GrowthDashboard({ session }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortKey, setSortKey] = useState('totalDollar');
    const [sortDir, setSortDir] = useState('desc');
    const [selectedId, setSelectedId] = useState(null);
    const [chartMetric, setChartMetric] = useState('audits');

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const res = await fetch(API + '/api/admin/growth/summary', { headers });
            if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
            setData(await res.json());
        } catch (e) { setErr(e.message); }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const customers = useMemo(() => data?.customers || [], [data]);
    const filtered = useMemo(() => {
        let rows = customers;
        const q = search.trim().toLowerCase();
        if (q) rows = rows.filter(r =>
            [r.company_name, r.contact_name, r.email].filter(Boolean).some(v => v.toLowerCase().includes(q)));
        if (typeFilter !== 'all') rows = rows.filter(r => r.customer_type === typeFilter);
        if (statusFilter !== 'all') rows = rows.filter(r => r.status === statusFilter);
        const dir = sortDir === 'asc' ? 1 : -1;
        rows = [...rows].sort((a, b) => {
            const va = a[sortKey], vb = b[sortKey];
            if (typeof va === 'string' || typeof vb === 'string') return ((va || '') > (vb || '') ? 1 : -1) * dir;
            return ((va || 0) - (vb || 0)) * dir;
        });
        return rows;
    }, [customers, search, typeFilter, statusFilter, sortKey, sortDir]);

    const sortBtn = (key) => () => {
        if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDir('desc'); }
    };
    const caret = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    if (loading) return <div className="history-loading"><div className="spinner" /></div>;
    if (err) return <div className="gd-error">Failed to load Growth Dashboard: {err} <button className="history-back-link" onClick={load}>Retry</button></div>;
    if (!data) return null;

    const k = data.kpis, f = data.funnel, c = data.charts;
    const lc = data.lifecycle || {};
    const rt = data.runtime || {};
    const opp = data.opportunity || {};
    const followUps = data.followUps || [];
    const beta = data.betaEngagement || [];
    const runtimeTracked = data.meta?.runtimeTracked;

    return (
        <div className="gd-root">
            <div className="gd-toolbar">
                <div className="gd-generated">Live · generated {fmtDateTime(data.generated_at)} · {data.meta.totalAuthUsers} auth users</div>
                <button className="history-back-link" onClick={load}>↻ Refresh</button>
            </div>

            {/* ── Hero cards (Part 4): the first things I see ── */}
            <div className="gd-hero-grid">
                <KpiCard label="Revenue Protected" value={fmt$(k.totalOpportunityDollars)} accent="var(--green)" />
                <KpiCard label="Average Revenue Protected Per Audit" value={fmt$(k.avgOpportunityDollars)} accent="var(--green)" />
                <KpiCard label="Total Audits" value={fmtNum(k.totalAudits)} accent="var(--amber)" />
                <KpiCard label="Active Customers" value={fmtNum(lc.active)} accent="var(--green)" />
                <KpiCard label="Needs Follow Up" value={fmtNum(followUps.length)} accent="var(--red,#ef4444)" />
            </div>

            {/* ── Lifecycle (Part 3) ── */}
            <div className="gd-section-title">Customer Lifecycle</div>
            <div className="gd-life-grid">
                <KpiCard label="Invited" value={fmtNum(lc.invited)} accent="var(--text-muted)" sub="no account yet" />
                <KpiCard label="Signed Up" value={fmtNum(lc.signedUp)} accent="var(--blue)" sub="accounts created" />
                <KpiCard label="Never Ran Audit" value={fmtNum(lc.neverRanAudit)} accent="var(--blue)" sub="signed up, 0 audits" />
                <KpiCard label="Active" value={fmtNum(lc.active)} accent="var(--green)" sub="audit in last 30d" />
                <KpiCard label="Dormant" value={fmtNum(lc.dormant)} accent="var(--amber)" sub="no audit 30d+" />
            </div>

            {/* ── Executive KPIs (Part 7) ── */}
            <div className="gd-section-title">Executive Metrics</div>
            <div className="gd-kpi-grid">
                <KpiCard label="Invites Sent" value={fmtNum(k.invitesSent)} />
                <KpiCard label="Accounts Created" value={fmtNum(k.accountsCreated)} accent="var(--blue)" />
                <KpiCard label="Active Users · 7d" value={fmtNum(k.activeUsers7)} accent="var(--green)" />
                <KpiCard label="Active Users · 30d" value={fmtNum(k.activeUsers30)} accent="var(--green)" />
                <KpiCard label="Total Audits" value={fmtNum(k.totalAudits)} accent="var(--amber)" />
                <KpiCard label="Reports Exported" value={fmtNum(k.reportsExported)} />
                <KpiCard label="Avg Audits / User" value={fmt1(k.avgAuditsPerUser)} />
                <KpiCard label="Revenue Protected" value={fmt$(k.totalOpportunityDollars)} accent="var(--green)" />
                <KpiCard label="Average Revenue Protected Per Audit" value={fmt$(k.avgOpportunityDollars)} accent="var(--green)" />
                <KpiCard label="Avg Revenue Protected / Customer" value={fmt$(k.avgOpportunityPerCustomer)} accent="var(--green)" />
                <KpiCard label="Largest Opportunity" value={fmt$(k.largestOpportunity)} accent="var(--green)" />
                <KpiCard label="Clean Estimates" value={fmtNum(k.cleanEstimates)} />
                <KpiCard label="Avg Findings / Audit" value={fmt1(k.avgFindingsPerAudit)} />
            </div>

            {/* ── Runtime (Part 6) ── */}
            <div className="gd-section-title">Audit Runtime {!runtimeTracked && <span className="gd-muted gd-tt-note">· no runtime-tracked audits yet (tracked going forward)</span>}</div>
            <div className="gd-kpi-grid">
                <KpiCard label="Average Runtime" value={fmtMs(rt.average)} accent="var(--blue)" />
                <KpiCard label="Fastest Audit" value={fmtMs(rt.fastest)} accent="var(--green)" />
                <KpiCard label="Slowest Audit" value={fmtMs(rt.slowest)} accent="var(--amber)" />
                <KpiCard label="Avg Runtime / Customer" value={fmtMs(rt.avgPerCustomer)} />
                <KpiCard label="Avg Runtime Overall" value={fmtMs(rt.overall)} />
                <KpiCard label="Runtime Samples" value={fmtNum(rt.tracked)} sub="audits with runtime" />
            </div>

            {/* ── Beta Funnel (Part 11) ── */}
            <div className="gd-section-title">Beta Funnel</div>
            <div className="gd-funnel">
                {[
                    ['Invites Sent', f.invitesSent],
                    ['Accounts Created', f.accountsCreated],
                    ['Logged In', f.usersLoggedIn],
                    ['Ran First Audit', f.usersWithFirstAudit],
                    ['Still Active (7d)', f.usersStillActive],
                    ['Lost (>14d)', f.usersLost],
                ].map(([label, val], i) => (
                    <div className="gd-funnel-step" key={label}>
                        <div className="gd-funnel-val" style={i === 5 ? { color: 'var(--red,#ef4444)' } : undefined}>{fmtNum(val)}</div>
                        <div className="gd-funnel-label">{label}</div>
                        {i < 5 && <div className="gd-funnel-arrow">→</div>}
                    </div>
                ))}
            </div>
            <div className="gd-funnel-meta">
                Avg time signup → first audit: <b>{fmtDuration(f.avgTimeToFirstAuditMs)}</b>
                &nbsp;·&nbsp; Avg audits / user: <b>{fmt1(f.avgAuditsPerUser)}</b>
            </div>

            {/* ── Charts (Part 14) ── */}
            <div className="gd-section-title">Trends · last 30 days</div>
            <div className="gd-chart-card">
                <div className="gd-chart-tabs">
                    {[['audits', 'Audits / Day'], ['dollars', 'Opportunity $ / Day'], ['activeUsers', 'Active Users / Day']].map(([key, lbl]) => (
                        <button key={key} className={'gd-chip ' + (chartMetric === key ? 'active' : '')} onClick={() => setChartMetric(key)}>{lbl}</button>
                    ))}
                </div>
                <LineChart data={c.series} valueKey={chartMetric}
                    color={chartMetric === 'dollars' ? 'var(--green)' : chartMetric === 'activeUsers' ? 'var(--blue)' : 'var(--amber)'}
                    money={chartMetric === 'dollars'} />
            </div>

            <div className="gd-chart-row">
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Most Active Customers</div>
                    <BarList rows={c.topCustomersByAudits} valueKey="audits" />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Opportunity $ by Customer</div>
                    <BarList rows={c.topCustomersByDollars} valueKey="dollars" money color="var(--green)" />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Opportunity $ by Company</div>
                    <BarList rows={c.companies} valueKey="dollars" labelKey="company" money color="var(--green)" />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Clean vs Findings</div>
                    <Donut a={c.cleanVsFindings.clean} b={c.cleanVsFindings.withFindings} labelA="Clean" labelB="With findings" />
                </div>
            </div>

            {/* ── Opportunity Intelligence (Part 4) ── */}
            <div className="gd-section-title">Opportunity Intelligence</div>
            <div className="gd-kpi-grid">
                <KpiCard label="Revenue Protected" value={fmt$(opp.total)} accent="var(--green)" />
                <KpiCard label="Average Revenue Protected Per Audit" value={fmt$(opp.average)} accent="var(--green)" />
                <KpiCard label="Avg Revenue Protected / Customer" value={fmt$(opp.averagePerCustomer)} accent="var(--green)" />
                <KpiCard label="Largest Opportunity Ever" value={fmt$(opp.largest)} accent="var(--green)" />
            </div>
            <div className="gd-chart-row">
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Top 10 Customers by Opportunity $</div>
                    <BarList rows={opp.topCustomers} valueKey="dollars" money color="var(--green)" max={10} />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Top 10 Companies by Opportunity $</div>
                    <BarList rows={opp.topCompanies} valueKey="dollars" labelKey="company" money color="var(--green)" max={10} />
                </div>
            </div>
            <div className="gd-chart-row">
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Opportunity $ / Day · last 30 days</div>
                    <LineChart data={opp.perDay} valueKey="dollars" color="var(--green)" money />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Opportunity $ / Month · last 12 months</div>
                    <MonthBars data={opp.perMonth} valueKey="dollars" color="var(--green)" money />
                </div>
            </div>

            {/* ── Monthly Charts (Part 8) ── */}
            <div className="gd-section-title">By Month · last 12 months</div>
            <div className="gd-chart-row">
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Opportunity Dollars by Month</div>
                    <MonthBars data={c.months} valueKey="dollars" color="var(--green)" money />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Audit Volume by Month</div>
                    <MonthBars data={c.months} valueKey="audits" color="var(--amber)" />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">New Customers by Month</div>
                    <MonthBars data={c.months} valueKey="newCustomers" color="var(--blue)" />
                </div>
            </div>
            <div className="gd-chart-row">
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Most Active Beta Users</div>
                    <BarList rows={c.topBetaUsers} valueKey="audits" max={10} />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Most Active Companies</div>
                    <BarList rows={c.companies} valueKey="audits" labelKey="company" max={10} />
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Dormant Customers <span className="gd-muted">({(c.dormant || []).length})</span></div>
                    <div className="gd-mini-list">
                        {(c.dormant || []).slice(0, 10).map(d => (
                            <div className="gd-mini-row" key={d.id} onClick={() => setSelectedId(d.id)}>
                                <span className="gd-mini-label">{d.label}</span>
                                <span className="gd-mini-meta">{d.daysSinceActivity != null ? `${d.daysSinceActivity}d ago` : '—'}</span>
                            </div>
                        ))}
                        {!(c.dormant || []).length && <div className="gd-chart-empty">None</div>}
                    </div>
                </div>
                <div className="gd-chart-card">
                    <div className="gd-chart-h">Never Ran Audit <span className="gd-muted">({(c.neverRan || []).length})</span></div>
                    <div className="gd-mini-list">
                        {(c.neverRan || []).slice(0, 10).map(d => (
                            <div className="gd-mini-row" key={d.id} onClick={() => setSelectedId(d.id)}>
                                <span className="gd-mini-label">{d.label}</span>
                                <span className="gd-mini-meta">{d.inviteAgeDays != null ? `invited ${d.inviteAgeDays}d ago` : '—'}</span>
                            </div>
                        ))}
                        {!(c.neverRan || []).length && <div className="gd-chart-empty">None</div>}
                    </div>
                </div>
            </div>

            {/* ── Follow-up Queue (Part 10) ── */}
            <div className="gd-section-title">Needs Follow Up <span className="gd-muted">({followUps.length})</span></div>
            {followUps.length ? (
                <div className="admin-table-wrap">
                    <table className="admin-table gd-table">
                        <thead><tr><th>Priority</th><th>Customer</th><th>Contact</th><th>Status</th><th>Reason</th></tr></thead>
                        <tbody>
                            {followUps.map(r => (
                                <tr key={r.id} className="gd-row gd-followup-row" onClick={() => setSelectedId(r.id)}>
                                    <td><span className="gd-followup-badge">{r.bucket}</span></td>
                                    <td>{r.label}</td>
                                    <td>{r.contact_name || r.email || '—'}</td>
                                    <td><span className={'gd-status gd-status-' + (r.status || '').toLowerCase().replace(/\s/g, '-')}>{r.status}</span></td>
                                    <td className="gd-followup-reason">{r.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : <div className="gd-empty-note">Nobody needs follow-up right now. ✓</div>}

            {/* ── Beta Engagement (Part 5) ── */}
            <div className="gd-section-title">Beta Engagement <span className="gd-muted">({beta.length})</span></div>
            <div className="admin-table-wrap">
                <table className="admin-table gd-table">
                    <thead>
                        <tr>
                            <th>Company</th><th>Contact</th><th>Status</th>
                            <th className="gd-num">Audits</th><th className="gd-num">Reports</th>
                            <th className="gd-num">Largest Est.</th><th className="gd-num">Largest Opp.</th>
                            <th className="gd-num">Total Opp. $</th><th className="gd-num">Avg Find.</th>
                            <th>Last Activity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {beta.map(r => (
                            <tr key={r.id} className={'gd-row' + (r.staleOver14d ? ' gd-row-stale' : '')} onClick={() => setSelectedId(r.id)}>
                                <td>
                                    {r.company_name || '—'}
                                    {r.staleOver14d && <span className="gd-stale-flag" title="Not returned in 14+ days">⚠ 14d+</span>}
                                    {r.neverRanAudit && <span className="gd-neverran-flag" title="Never ran an audit">never ran</span>}
                                </td>
                                <td>{r.contact_name || r.email || '—'}</td>
                                <td><span className={'gd-status gd-status-' + (r.status || '').toLowerCase().replace(/\s/g, '-')}>{r.status}</span></td>
                                <td className="gd-num">{fmtNum(r.audits)}</td>
                                <td className="gd-num">{fmtNum(r.reportsExported)}</td>
                                <td className="gd-num">{r.largestEstimate > 0 ? fmt$(r.largestEstimate) : '—'}</td>
                                <td className="gd-num">{r.largestOpportunity > 0 ? fmt$(r.largestOpportunity) : '—'}</td>
                                <td className="gd-num" style={{ color: r.totalDollar > 0 ? 'var(--green)' : undefined }}>{r.totalDollar > 0 ? fmt$(r.totalDollar) : '—'}</td>
                                <td className="gd-num">{r.audits ? fmt1(r.avgFindings) : '—'}</td>
                                <td>{fmtDate(r.lastActivity)}</td>
                            </tr>
                        ))}
                        {!beta.length && <tr><td colSpan={10} className="gd-muted">No beta customers yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* ── Customer Table (Part 8) ── */}
            <div className="gd-section-title">All Customers</div>
            <div className="gd-filters">
                <input className="admin-plan-select gd-search" placeholder="Search company, contact, email…" value={search} onChange={e => setSearch(e.target.value)} />
                <select className="admin-plan-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="all">All Types</option>
                    <option value="beta">Beta</option><option value="trial">Trial</option>
                    <option value="paid">Paid</option><option value="enterprise">Enterprise</option>
                </select>
                <select className="admin-plan-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Statuses</option>
                    <option value="Invited">Invited</option>
                    <option value="Never Ran Audit">Never Ran Audit</option>
                    <option value="Active">Active</option>
                    <option value="Dormant">Dormant</option>
                </select>
                <div className="gd-filter-count">{filtered.length} of {customers.length}</div>
            </div>
            <div className="admin-table-wrap">
                <table className="admin-table gd-table">
                    <thead>
                        <tr>
                            <th onClick={sortBtn('company_name')} className="gd-sortable">Company{caret('company_name')}</th>
                            <th>Contact</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th onClick={sortBtn('totalAudits')} className="gd-sortable gd-num">Audits{caret('totalAudits')}</th>
                            <th onClick={sortBtn('reportsExported')} className="gd-sortable gd-num">Reports{caret('reportsExported')}</th>
                            <th onClick={sortBtn('totalDollar')} className="gd-sortable gd-num">Opportunity $ {caret('totalDollar')}</th>
                            <th onClick={sortBtn('largestEstimate')} className="gd-sortable gd-num">Largest Est.{caret('largestEstimate')}</th>
                            <th onClick={sortBtn('avgFindings')} className="gd-sortable gd-num">Avg Find.{caret('avgFindings')}</th>
                            <th className="gd-num">Avg Runtime</th>
                            <th onClick={sortBtn('lastActivity')} className="gd-sortable">Last Activity{caret('lastActivity')}</th>
                            <th>First Audit</th>
                            <th>Last Audit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(r => (
                            <tr key={r.id} className="gd-row" onClick={() => setSelectedId(r.id)}>
                                <td>{r.company_name || '—'}{r.open_feedback > 0 && <span className="gd-flag" title={`${r.open_feedback} open feedback`}>●</span>}</td>
                                <td>{r.contact_name || r.display_name || '—'}</td>
                                <td><span className={'gd-type gd-type-' + r.customer_type}>{r.customer_type}</span></td>
                                <td><span className={'gd-status gd-status-' + r.status.toLowerCase().replace(/\s/g, '-')}>{r.status}</span></td>
                                <td className="gd-num">{fmtNum(r.totalAudits)}</td>
                                <td className="gd-num">{fmtNum(r.reportsExported)}</td>
                                <td className="gd-num" style={{ color: r.totalDollar > 0 ? 'var(--green)' : undefined, fontWeight: r.totalDollar > 0 ? 700 : undefined }}>{r.totalDollar > 0 ? fmt$(r.totalDollar) : '—'}</td>
                                <td className="gd-num">{r.largestEstimate > 0 ? fmt$(r.largestEstimate) : '—'}</td>
                                <td className="gd-num">{r.totalAudits ? fmt1(r.avgFindings) : '—'}</td>
                                <td className="gd-num">{fmtMs(r.avgRuntimeMs)}</td>
                                <td>{fmtDate(r.lastActivity)}</td>
                                <td>{fmtDate(r.firstAudit)}</td>
                                <td>{fmtDate(r.lastAudit)}</td>
                            </tr>
                        ))}
                        {!filtered.length && <tr><td colSpan={13} className="gd-muted">No customers match.</td></tr>}
                    </tbody>
                </table>
            </div>

            {selectedId && <CustomerDrawer id={selectedId} headers={headers} onClose={() => setSelectedId(null)} onChanged={load} />}
        </div>
    );
}

// ── Customer Detail drawer (Part 9 + Part 13 feedback CRM) ───────────────────
function CustomerDrawer({ id, headers, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fb, setFb] = useState({ category: 'general', priority: 'medium', body: '' });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await fetch(API + `/api/admin/growth/customer/${id}`, { headers });
            setDetail(await res.json());
        } catch (e) { /* noop */ }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

    const addFeedback = async () => {
        if (!fb.body.trim()) return;
        setSaving(true);
        await fetch(API + '/api/admin/growth/feedback', {
            method: 'POST', headers, body: JSON.stringify({ customer_id: id, ...fb }),
        });
        setFb({ category: 'general', priority: 'medium', body: '' });
        setSaving(false);
        load();
    };
    const cycleStatus = async (item) => {
        const next = item.status === 'Open' ? 'In Progress' : item.status === 'In Progress' ? 'Resolved' : 'Open';
        await fetch(API + `/api/admin/growth/feedback/${item.id}`, { method: 'PATCH', headers, body: JSON.stringify({ status: next }) });
        load();
    };

    const c = detail?.customer;
    const stat = (label, val) => <div className="gd-d-stat"><span>{label}</span><b>{val}</b></div>;

    return (
        <div className="gd-drawer-overlay" onClick={onClose}>
            <div className="gd-drawer" onClick={e => e.stopPropagation()}>
                <div className="gd-drawer-head">
                    <div>
                        <div className="gd-drawer-title">{c?.company_name || detail?.raw?.company_name || 'Customer'}</div>
                        <div className="gd-drawer-sub">{c?.contact_name || c?.display_name || '—'} · {detail?.email || c?.email || 'no email'}</div>
                    </div>
                    <button className="history-back-link" onClick={onClose}>✕ Close</button>
                </div>

                {loading && <div className="history-loading"><div className="spinner" /></div>}
                {!loading && detail && (
                    <div className="gd-drawer-body">
                        <div className="gd-d-section">
                            <div className="gd-d-grid">
                                {stat('Type', <span className={'gd-type gd-type-' + c?.customer_type}>{c?.customer_type}</span>)}
                                {stat('Status', <span className={'gd-status gd-status-' + (c?.status || '').toLowerCase().replace(/\s/g, '-')}>{c?.status}</span>)}
                                {stat('Plan', c?.plan || '—')}
                                {stat('Invite Date', fmtDate(c?.invite_date))}
                                {stat('Join Date', fmtDate(c?.joinDate))}
                                {stat('First Audit', fmtDate(c?.firstAudit))}
                                {stat('Last Audit', fmtDate(c?.lastAudit))}
                                {stat('Last Login', fmtDateTime(c?.lastLogin))}
                                {stat('TinyURL', c?.tinyurl ? <a href={c.tinyurl} target="_blank" rel="noreferrer" className="gd-link">{c.tinyurl.replace('https://', '')}</a> : '—')}
                                {stat('Invite URL', c?.invite_url ? <a href={c.invite_url} target="_blank" rel="noreferrer" className="gd-link">/r/{c.invite_slug}</a> : '—')}
                                {stat('Invite Clicks', fmtNum(c?.invite_clicks))}
                            </div>
                        </div>

                        <div className="gd-d-section">
                            <div className="gd-d-h">Statistics</div>
                            <div className="gd-d-grid">
                                {stat('Total Audits', fmtNum(c?.totalAudits))}
                                {stat('Reports Exported', fmtNum(c?.reportsExported))}
                                {stat('PDFs Uploaded', fmtNum(c?.pdfsUploaded))}
                                {stat('Clean Estimates', fmtNum(c?.cleanEstimates))}
                                {stat('Avg Findings', c?.totalAudits ? fmt1(c?.avgFindings) : '—')}
                                {stat('Avg Runtime', fmtMs(c?.avgRuntimeMs))}
                                {stat('Total Opportunity', <span style={{ color: 'var(--green)' }}>{fmt$(c?.totalDollar)}</span>)}
                                {stat('Avg Opportunity', fmt$(c?.avgDollar))}
                                {stat('Largest Opportunity', fmt$(c?.largestOpportunity))}
                                {stat('Largest Estimate', fmt$(c?.largestEstimate))}
                            </div>
                        </div>

                        {/* CRM edit */}
                        <div className="gd-d-section">
                            <div className="gd-d-h">CRM</div>
                            <CrmEditor customer={detail.raw} headers={headers} onSaved={() => { load(); onChanged && onChanged(); }} />
                        </div>

                        {/* Feedback (Part 13) */}
                        <div className="gd-d-section">
                            <div className="gd-d-h">Feedback &amp; Notes <span className="gd-muted">(internal only)</span></div>
                            <div className="gd-fb-form">
                                <select className="admin-plan-select" value={fb.category} onChange={e => setFb({ ...fb, category: e.target.value })}>
                                    <option value="false_positive">False Positive</option>
                                    <option value="feature_request">Feature Request</option>
                                    <option value="general">General Note</option>
                                </select>
                                <select className="admin-plan-select" value={fb.priority} onChange={e => setFb({ ...fb, priority: e.target.value })}>
                                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                                </select>
                                <input className="admin-plan-select gd-fb-input" placeholder="Add a note…" value={fb.body}
                                    onChange={e => setFb({ ...fb, body: e.target.value })} onKeyDown={e => e.key === 'Enter' && addFeedback()} />
                                <button className="history-back-link" disabled={saving || !fb.body.trim()} onClick={addFeedback}>{saving ? '…' : 'Add'}</button>
                            </div>
                            <div className="gd-fb-list">
                                {(detail.feedback || []).map(item => (
                                    <div className="gd-fb-item" key={item.id}>
                                        <span className={'gd-fb-cat gd-fb-' + item.category}>{item.category.replace('_', ' ')}</span>
                                        <span className={'gd-fb-pri gd-pri-' + item.priority}>{item.priority}</span>
                                        <span className="gd-fb-body">{item.body}</span>
                                        <button className={'gd-fb-status gd-status-' + item.status.toLowerCase().replace(/\s/g, '-')} onClick={() => cycleStatus(item)}>{item.status}</button>
                                        <span className="gd-fb-date">{fmtDate(item.created_at)}</span>
                                    </div>
                                ))}
                                {!(detail.feedback || []).length && <div className="gd-muted">No feedback logged yet.</div>}
                            </div>
                        </div>

                        {/* Timeline (Part 9) */}
                        <div className="gd-d-section">
                            <div className="gd-d-h">Recent Activity</div>
                            <div className="gd-timeline">
                                {(detail.timeline || []).map((ev, i) => (
                                    <div className="gd-tl-item" key={i}>
                                        <div className="gd-tl-dot" />
                                        <div className="gd-tl-body">
                                            <span className="gd-tl-type">{ev.type}</span>
                                            {ev.note && <span className="gd-tl-note">{ev.note}</span>}
                                        </div>
                                        <div className="gd-tl-time">{fmtDateTime(ev.at)}</div>
                                    </div>
                                ))}
                                {!(detail.timeline || []).length && <div className="gd-muted">No activity yet.</div>}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function CrmEditor({ customer, headers, onSaved }) {
    const [form, setForm] = useState({
        company_name: customer.company_name || '', contact_name: customer.contact_name || '',
        email: customer.email || '', customer_type: customer.customer_type || 'beta',
        status: customer.status || 'Invited', notes: customer.notes || '',
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const save = async () => {
        setSaving(true); setSaved(false);
        await fetch(API + `/api/admin/growth/customer/${customer.id}`, { method: 'PATCH', headers, body: JSON.stringify(form) });
        setSaving(false); setSaved(true); onSaved && onSaved();
        setTimeout(() => setSaved(false), 2000);
    };
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    return (
        <div className="gd-crm">
            <div className="gd-crm-row">
                <input className="admin-plan-select" placeholder="Company" value={form.company_name} onChange={set('company_name')} />
                <input className="admin-plan-select" placeholder="Contact" value={form.contact_name} onChange={set('contact_name')} />
                <input className="admin-plan-select" placeholder="Email (enables auto-match)" value={form.email} onChange={set('email')} />
            </div>
            <div className="gd-crm-row">
                <select className="admin-plan-select" value={form.customer_type} onChange={set('customer_type')}>
                    <option value="beta">Beta</option><option value="trial">Trial</option><option value="paid">Paid</option><option value="enterprise">Enterprise</option>
                </select>
                <select className="admin-plan-select" value={form.status} onChange={set('status')} title="Status is normally auto-derived from activity">
                    <option value="Invited">Invited</option>
                    <option value="Never Ran Audit">Never Ran Audit</option>
                    <option value="Active">Active</option>
                    <option value="Dormant">Dormant</option>
                </select>
                <button className="history-back-link" onClick={save} disabled={saving}>{saving ? 'Saving…' : saved ? '✓ Saved' : 'Save CRM'}</button>
            </div>
            <textarea className="admin-plan-select gd-crm-notes" placeholder="Notes…" value={form.notes} onChange={set('notes')} rows={2} />
        </div>
    );
}
